import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import BoostPlans from "@/components/BoostPlans";
import {
    Rocket,
    CheckCircle,
    Bell,
    Clock,
    Sparkle,
    WhatsappLogo,
    Envelope,
} from "@phosphor-icons/react";

const INR = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;

// Candidate-facing page to purchase a Boost plan and see current status.
// Razorpay Checkout runs inline (modal) — no post-redirect polling needed.
export default function CandidateBoost() {
    const [boost, setBoost] = useState(null);
    const [params] = useSearchParams();
    const cancelled = params.get("cancelled");

    const loadBoost = React.useCallback(() => {
        api
            .get("/candidate/boost/me")
            .then((r) => setBoost(r.data))
            .catch(() => setBoost({ active: false }));
    }, []);

    useEffect(() => {
        loadBoost();
    }, [loadBoost]);

    useEffect(() => {
        if (cancelled) toast("Checkout cancelled. No charge was made.");
    }, [cancelled]);

    return (
        <div data-testid="candidate-boost-page" className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
            <div className="mb-6 sm:mb-10">
                <p className="text-[10px] font-black uppercase tracking-[0.25em] text-cobalt inline-flex items-center gap-2 mb-2">
                    <Rocket size={12} weight="fill" /> Candidate Boost
                </p>
                <h1 className="font-heading text-2xl sm:text-4xl font-bold tracking-tight">
                    Skip the noise. Get the roles that actually match you.
                </h1>
                <p className="text-sm text-[#525252] mt-3 max-w-2xl">
                    Recruiters see you first, alerts hit your inbox, WhatsApp, and app the
                    moment a matching JD opens. Cancel anytime.
                </p>
            </div>

            {/* Current status */}
            <StatusCard boost={boost} />

            {/* Plans */}
            <div className="mt-6 sm:mt-8">
                <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight mb-4">
                    {boost?.active ? "Upgrade or extend" : "Pick your boost"}
                </h2>
                <BoostPlans
                    variant="candidate"
                    currentPlanId={boost?.plan_id || null}
                    onPurchased={loadBoost}
                />
            </div>

            {/* Why boost */}
            <div className="mt-10 sm:mt-16 grid gap-4 sm:grid-cols-3">
                {[
                    {
                        icon: Bell,
                        title: "Alerts you actually read",
                        copy: "One email, one WhatsApp ping, one in-app badge — for real matches, not spam.",
                    },
                    {
                        icon: Sparkle,
                        title: "Featured in recruiter search",
                        copy: "Boosted candidates sit above free profiles with a gold badge on every list.",
                    },
                    {
                        icon: Clock,
                        title: "Save days of scrolling",
                        copy: "Roles come to you in the exact seniority, location, and stack you saved.",
                    },
                ].map(({ icon: Icon, title, copy }) => (
                    <div key={title} className="bg-white border border-[#E5E5E5] p-5">
                        <Icon size={22} weight="bold" className="text-cobalt mb-3" />
                        <p className="font-heading font-bold text-sm mb-1">{title}</p>
                        <p className="text-[13px] text-[#525252] leading-relaxed">{copy}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

function StatusCard({ boost }) {
    if (!boost || !boost.active) {
        return (
            <div
                data-testid="boost-status-inactive"
                className="bg-[#FAFAFA] border border-[#E5E5E5] p-5 sm:p-6"
            >
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252]">
                    No active boost
                </p>
                <p className="font-heading text-lg font-bold mt-1">
                    You&apos;re on the free plan.
                </p>
                <p className="text-xs text-[#525252] mt-1">
                    Free profiles can save searches but don&apos;t receive email/WhatsApp alerts and aren&apos;t
                    prioritized in recruiter search.
                </p>
            </div>
        );
    }
    const unlimited = boost.alerts_limit === -1;
    return (
        <div
            data-testid="boost-status-active"
            className="bg-ink text-white p-5 sm:p-6"
        >
            <div className="flex items-center gap-2 mb-2">
                <CheckCircle size={14} weight="fill" className="text-splash" />
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-splash">
                    Boost active
                </span>
            </div>
            <p className="font-heading text-2xl font-bold">{boost.plan_name}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mt-5">
                <Stat
                    label="Alerts used"
                    value={
                        unlimited
                            ? `${boost.alerts_used || 0} · Unlimited`
                            : `${boost.alerts_used || 0} / ${boost.alerts_limit}`
                    }
                    testid="boost-alerts-used"
                />
                <Stat
                    label="Alerts remaining"
                    value={
                        unlimited
                            ? "Unlimited"
                            : (boost.alerts_remaining ?? 0)
                    }
                    testid="boost-alerts-remaining"
                />
                <Stat
                    label="Days remaining"
                    value={boost.days_remaining ?? 0}
                    testid="boost-days-remaining"
                />
            </div>
            <div className="mt-5 pt-4 border-t border-white/10 flex flex-wrap items-center gap-4 text-xs text-white/70">
                <span className="inline-flex items-center gap-1.5">
                    <Envelope size={12} weight="fill" className="text-splash" /> Email alerts
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <WhatsappLogo size={12} weight="fill" className="text-splash" /> WhatsApp alerts
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <Bell size={12} weight="fill" className="text-splash" /> In-app notifications
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <Sparkle size={12} weight="fill" className="text-splash" /> Featured badge
                </span>
            </div>
        </div>
    );
}

function Stat({ label, value, testid }) {
    return (
        <div data-testid={testid}>
            <p className="text-[9px] font-black uppercase tracking-[0.2em] text-white/50">
                {label}
            </p>
            <p className="font-heading text-2xl font-black tracking-tighter mt-1">
                {value}
            </p>
        </div>
    );
}
